import { useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { coreApi } from '@/api';
import { makeFormData, checkFileSize, maxDefaultFileSize, toast } from '@/utils';

export type FileUploadType = 'image' | 'bgm' | 'file';

export interface FileUploadOptions {
  type: FileUploadType;
  multiple?: boolean;
  maxFileSize?: number;
  directory?: string;
  useFor?: string;
  onSuccess?: (files: any[]) => void;
  onError?: (error: any) => void;
}

export interface UseFileUploadReturn {
  isLoading: boolean;
  uploadedFiles: any[];
  uploadFiles: (files: File[]) => Promise<void>;
  clearFiles: () => void;
}

export function useFileUpload(options: FileUploadOptions): UseFileUploadReturn {
  const {
    type,
    multiple = false,
    maxFileSize = maxDefaultFileSize,
    directory,
    useFor,
    onSuccess,
    onError
  } = options;

  const { appId, projectId } = useParams<AppParam>();
  const [isLoading, setIsLoading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);

  const getUploadEndpoint = useCallback(() => {
    switch (type) {
      case 'image':
        return `/apps/${appId}/images/uploadFile`;
      case 'bgm':
      case 'file':
        return `/apps/${appId}/projects/${projectId}/files`;
      default:
        throw new Error(`Unsupported upload type: ${type}`);
    }
  }, [type, appId, projectId]);

  const createFormData = useCallback((file: File) => {
    switch (type) {
      case 'image':
        return makeFormData({ file });
      case 'bgm':
        return makeFormData({ 
          file, 
          dir: directory || '.musicfiles' 
        });
      case 'file':
        return makeFormData({ 
          file, 
          ...(directory && { dir: directory })
        });
      default:
        return makeFormData({ file });
    }
  }, [type, directory]);

  const validateFiles = useCallback((files: File[]) => {
    if (!multiple && files.length > 1) {
      toast({ message: '하나의 파일만 올릴 수 있어요', type: 'error' });
      return false;
    }

    for (const file of files) {
      if (checkFileSize({ file, maxFileSize })) {
        toast({
          message: `파일 크기는 ${maxFileSize / 1024 / 1024}MB 이하여야 해요`,
          type: 'error'
        });
        return false;
      }
    }

    return true;
  }, [multiple, maxFileSize]);

  const uploadFiles = useCallback(async (files: File[]) => {
    if (!validateFiles(files)) {
      return;
    }

    setIsLoading(true);

    try {
      const uploadPromises = files.map(async (file) => {
        const formData = createFormData(file);
        const params = type === 'image' && useFor ? { useFor } : {};
        
        const response = await coreApi.post(
          getUploadEndpoint(),
          formData,
          { params }
        );
        
        return response.data;
      });

      const results = await Promise.all(uploadPromises);
      
      if (multiple) {
        setUploadedFiles(prev => [...prev, ...results]);
      } else {
        setUploadedFiles(results);
      }

      onSuccess?.(results);
    } catch (error) {
      console.error('File upload error:', error);
      toast({ message: '파일 업로드에 실패했습니다', type: 'error' });
      onError?.(error);
    } finally {
      setIsLoading(false);
    }
  }, [validateFiles, createFormData, getUploadEndpoint, type, useFor, multiple, onSuccess, onError]);

  const clearFiles = useCallback(() => {
    setUploadedFiles([]);
  }, []);

  return {
    isLoading,
    uploadedFiles,
    uploadFiles,
    clearFiles
  };
}